<template>
  <div class="lead-detail-drawer" :class="{ 'lead-detail-drawer--open': show }">
    <div class="lead-detail-drawer__overlay"></div>

    <div class="lead-detail-drawer__content">
      <div class="lead-detail-drawer__header">
        <div class="lead-detail-drawer__header-content">
          <div class="lead-detail-drawer__title-section">
            <div
              class="lead-detail-drawer__icon"
              :class="{ 'lead-detail-drawer__icon--company': isCompany }"
            >
              <BaseIcon :name="isCompany ? 'building' : 'user'" size="20" />
            </div>
            <div class="lead-detail-drawer__titles">
              <div class="lead-detail-drawer__subtitle">
                {{ customerTypeLabel }}
              </div>
              <div class="lead-detail-drawer__title-row">
                <span class="lead-detail-drawer__title">{{ drawerTitle }}</span>
                <span v-if="isCompany" class="lead-detail-drawer__type-badge">
                  <BaseIcon name="building" size="11" />
                  {{ $t('lead_details.customer_type.company') }}
                </span>
              </div>
            </div>
          </div>

          <div class="lead-detail-drawer__actions">
            <Btn variant="plain" size="sm" @click="openFullPage" v-if="leadId">
              <BaseIcon name="arrow-up-right" size="14" />
            </Btn>

            <Btn variant="plain" size="sm" @click="$emit('close')">
              <BaseIcon name="xmark" size="16" />
            </Btn>
          </div>
        </div>
      </div>

      <div class="lead-detail-drawer__body">
        <LeadDetailContent
          v-if="leadId"
          :lead-id="leadId"
          @lead-updated="handleLeadUpdated"
          @lead-archived="handleLeadArchived"
          @lead-deleted="handleLeadDeleted"
        />

        <div v-else class="lead-detail-drawer__empty">
          <BaseIcon name="user" size="48" />
          <h3>{{ $t('lead_details.no_lead_selected') }}</h3>
          <p>{{ $t('lead_details.select_lead_to_view') }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { defineComponent, computed, getCurrentInstance } from 'vue';
  import { useRouter } from 'vue-router';
  import { useCrmStore } from '@/store/crmStore';

  import LeadDetailContent from '@/components/crm/LeadDetailContent.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import Btn from '@/components/partials/Btn.vue';

  export default defineComponent({
    name: 'LeadDetailDrawer',

    components: {
      LeadDetailContent,
      BaseIcon,
      Btn,
    },

    props: {
      show: {
        type: Boolean,
        default: false,
      },
      leadId: {
        type: String,
        default: null,
      },
    },

    emits: ['close', 'lead-updated', 'lead-archived', 'lead-deleted'],

    setup(props, { emit }) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;
      const router = useRouter();
      const crmStore = useCrmStore();

      const lead = computed(() => crmStore.currentLead);
      const isCompany = computed(() => lead.value?.customer_type === 'company');

      const drawerTitle = computed(() => {
        if (isCompany.value && lead.value?.company_name) {
          return lead.value.company_name;
        }
        if (lead.value?.name) {
          return lead.value.name;
        }
        return props.leadId ? $t('lead_details.loading') : $t('lead_details.no_lead_selected');
      });

      const customerTypeLabel = computed(() => {
        if (!lead.value) return $t('crm.lead_details');
        return isCompany.value
          ? $t('lead_details.customer_type.company')
          : $t('lead_details.customer_type.person');
      });

      const openFullPage = () => {
        if (props.leadId) {
          router.push(`/crm/lead/${props.leadId}`);
          emit('close');
        }
      };

      const handleLeadUpdated = (updatedLead) => {
        emit('lead-updated', updatedLead);
      };

      const handleLeadArchived = (leadId) => {
        emit('close');
        emit('lead-archived', leadId);
      };

      const handleLeadDeleted = (leadId) => {
        emit('close');
        emit('lead-deleted', leadId);
      };

      return {
        drawerTitle,
        isCompany,
        customerTypeLabel,
        openFullPage,
        handleLeadUpdated,
        handleLeadArchived,
        handleLeadDeleted,
      };
    },
  });
</script>

<style scoped lang="scss">
  .lead-detail-drawer {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    pointer-events: none;
    opacity: 0;
    transition: opacity 0.3s ease;

    &--open {
      pointer-events: all;
      opacity: 1;
    }

    &__overlay {
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      cursor: pointer;
    }

    &__content {
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 90vw;
      max-width: 1200px;
      background: white;
      box-shadow: -4px 0 24px rgba(0, 0, 0, 0.15);
      display: flex;
      flex-direction: column;
      transform: translateX(100%);
      transition: transform 0.3s ease;

      @include lg-down {
        width: 100vw;
        max-width: none;
      }
    }

    &--open &__content {
      transform: translateX(0);
    }

    &__header {
      flex-shrink: 0;
      background: white;
      border-bottom: 1px solid var(--slate-200);
      padding: 1.5rem;
    }

    &__header-content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 1rem;
    }

    &__title-section {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      flex: 1;
      min-width: 0;
    }

    &__icon {
      flex-shrink: 0;
      width: 40px;
      height: 40px;
      background: var(--brand-50);
      color: var(--brand-600);
      border-radius: 0.5rem;
      display: flex;
      align-items: center;
      justify-content: center;

      &--company {
        background: var(--indigo-50);
        color: var(--indigo-600);
      }
    }

    &__titles {
      flex: 1;
      min-width: 0;
    }

    &__subtitle {
      font-size: 0.75rem;
      color: var(--slate-500);
      margin-bottom: 0.125rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      font-weight: 600;
    }

    &__title-row {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__title {
      font-size: 1.125rem;
      font-weight: 600;
      color: var(--slate-900);
    }

    &__type-badge {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--indigo-700);
      background: var(--indigo-50);
      border: 1px solid var(--indigo-100);
      padding: 0.125rem 0.5rem;
      border-radius: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.04em;
    }

    &__actions {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      flex-shrink: 0;
    }

    &__body {
      flex: 1;
      overflow-y: auto;
      padding: 1.5rem;
      background: var(--slate-50);
    }

    &__empty {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      padding: 4rem 2rem;
      color: var(--slate-500);

      .icon {
        color: var(--slate-300);
        margin-bottom: 1rem;
      }

      h3 {
        font-size: 1.125rem;
        font-weight: 600;
        color: var(--slate-900);
        margin: 0 0 0.5rem 0;
      }

      p {
        font-size: 0.875rem;
        margin: 0;
      }
    }
  }
</style>
